import { useRoute, Link } from "wouter";
import {
  useGetMyUpdate,
  getGetMyUpdateQueryKey,
} from "@workspace/api-client-react";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent } from "@workspace/ui-web/components/ui/card";
import { Badge } from "@workspace/ui-web/components/ui/badge";
import {
  Loader2,
  ArrowLeft,
  Calendar,
  Scale,
  Activity,
  Target,
} from "lucide-react";
import { format } from "date-fns";
import { getImageUrl } from "@/lib/utils";

export default function PortalUpdateDetail() {
  const [, params] = useRoute("/portal/updates/:id");
  const id = params?.id ? parseInt(params.id, 10) : 0;

  const { data: update, isLoading } = useGetMyUpdate(id, {
    query: { enabled: !!id, queryKey: getGetMyUpdateQueryKey(id) },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-muted/20">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!update) {
    return (
      <div className="min-h-screen flex flex-col bg-muted/20">
        <Navbar />
        <div className="flex-1 flex items-center justify-center flex-col gap-4">
          <h2 className="text-2xl font-bold">Update not found</h2>
          <Link href="/portal" className="text-primary hover:underline">
            Return to portal
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-muted/20">
      <Navbar />
      <main className="flex-1 container px-4 py-8 mx-auto max-w-3xl">
        <div className="mb-6">
          <Link
            href="/portal"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Portal
          </Link>
        </div>

        <div className="space-y-6">
          <div className="flex items-center gap-3 text-muted-foreground border-b pb-4">
            <Calendar className="h-5 w-5 text-primary" />
            <span className="text-lg font-medium text-foreground">
              {format(new Date(update.createdAt), "MMMM d, yyyy")}
            </span>
          </div>

          {update.weightLbs && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Scale className="h-5 w-5 text-secondary" />
              <span className="font-medium text-foreground">
                {update.weightLbs} lbs
              </span>
            </div>
          )}

          <div className="prose max-w-none">
            <p className="text-lg leading-relaxed whitespace-pre-wrap">
              {update.note}
            </p>
          </div>

          {update.milestones.length > 0 && (
            <div className="mt-8 space-y-4 pt-6 border-t">
              <h3 className="text-lg font-bold">Milestones & Health</h3>
              <div className="grid gap-3">
                {update.milestones.map((m, i) => (
                  <Card key={i} className="bg-card/50">
                    <CardContent className="p-4 flex gap-4 items-start">
                      {m.category === "health" ? (
                        <Activity className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                      ) : m.category === "training" ? (
                        <Target className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      ) : (
                        <div className="h-5 w-5 bg-muted rounded-full shrink-0 mt-0.5" />
                      )}
                      <div>
                        <Badge variant="outline" className="mb-2 capitalize">
                          {m.category}
                        </Badge>
                        <p className="text-foreground">{m.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {update.photos.length > 0 && (
            <div className="mt-8 space-y-4 pt-6 border-t">
              <h3 className="text-lg font-bold">Photos</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {update.photos.map((photoPath, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-xl overflow-hidden border bg-muted"
                  >
                    <img
                      src={getImageUrl(photoPath)}
                      alt={`Update photo ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
